import {
  AlertCircle,
  ChevronDown,
  ArrowRight,
  ImageIcon,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";

import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent } from "@runcheetah/ui";
import { cn } from "@/lib/utils";
import {
  CarouselSlideSummary,
  getCarouselReviewState,
} from "@/pages/social-post-planner/components/carousel-slide-review";
import { SceneTemplatePreview } from "@/pages/social-post-planner/components/scene-template-preview";

import type { CampaignReviewPlannedPost } from "./types";
import {
  getPlannedPostPrimaryActionState,
  getPostTypeLabel,
  PLANNED_POST_STATUS_CONFIG,
} from "./utils";

export function PlannedPostCard({
  post,
  selected,
  expanded,
  longDateLabel,
  onToggleDetails,
  onOpenWorkspace,
  onRegenerate,
}: {
  post: CampaignReviewPlannedPost;
  selected: boolean;
  expanded: boolean;
  longDateLabel: string;
  onToggleDetails: () => void;
  onOpenWorkspace: () => void;
  onRegenerate: () => void;
}) {
  const latestAttempt = post.output.latestAttempt;
  const latestCompleted = post.output.latestCompleted;
  const carouselState = getCarouselReviewState(latestCompleted?.carousel ?? null, null);
  const plan = carouselState.selectedPlan ?? latestCompleted?.result?.plan ?? null;
  const brandkit = latestCompleted?.preview?.brandkit ?? null;
  const previewAsset = carouselState.selectedAssets[0] ?? latestCompleted?.assets[0] ?? null;
  const linkedAssets = carouselState.isCarousel
    ? carouselState.selectedAssets
    : (latestCompleted?.assets ?? []);
  const objectiveText = post.slot.objectiveLabel ?? post.slot.objective;
  const audienceText = post.slot.audienceSegment ?? "General audience";
  const creativeObjectiveText = plan?.creativeDirection.objective ?? "No creative objective yet.";
  const rationaleText = plan?.creativeDirection.rationale ?? "No creative rationale yet.";
  const summaryText =
    latestCompleted?.summary ?? plan?.creativeDirection.summary ?? "No result summary yet.";
  const formatLabel = getPostTypeLabel(post.slot.postType);
  const actionState = getPlannedPostPrimaryActionState({
    postStatus: post.status,
    hasAttempt: post.attemptCount > 0,
    hasCompleted: Boolean(latestCompleted),
    hasActiveRun: latestAttempt?.status === "queued" || latestAttempt?.status === "processing",
  });
  const showsPreviousSuccessWarning = Boolean(
    latestCompleted &&
    latestAttempt &&
    latestAttempt.status === "failed" &&
    latestAttempt.generationTaskId !== latestCompleted.generationTaskId,
  );

  return (
    <Card
      className={cn(
        "overflow-hidden border-border/60 bg-card shadow-sm",
        selected && "border-primary/50 shadow-md",
      )}
    >
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className={cn(
              "text-[10px] uppercase tracking-[0.18em]",
              PLANNED_POST_STATUS_CONFIG[post.status]?.className,
            )}
          >
            {PLANNED_POST_STATUS_CONFIG[post.status]?.label ?? post.status}
          </Badge>
          <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/52">
            {longDateLabel}
          </span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/40">
            {post.attemptCount} {post.attemptCount === 1 ? "attempt" : "attempts"}
          </span>
        </div>

        <div className="space-y-1.5">
          <h3 className="text-balance text-lg font-semibold text-foreground">
            {post.slot.concept}
          </h3>
          {post.slot.callToAction ? (
            <p className="text-sm leading-6 text-foreground/66">{post.slot.callToAction}</p>
          ) : null}
        </div>

        <div className="-mx-4 overflow-hidden bg-muted/15 px-4 py-5 sm:-mx-5 sm:px-5 sm:py-6">
          {plan && brandkit ? (
            <SceneTemplatePreview
              plan={plan}
              linkedAssets={linkedAssets}
              brandkit={brandkit}
              showCanvasMeta={false}
              maxWidth={560}
            />
          ) : previewAsset ? (
            <img
              src={previewAsset.signedUrl}
              alt={`Generated campaign creative for ${post.slot.concept}`}
              width={previewAsset.width ?? 1080}
              height={previewAsset.height ?? 1350}
              className="mx-auto block h-auto w-full object-contain"
              loading="lazy"
            />
          ) : post.status === "generating" || post.status === "scheduled" ? (
            <EmptyCreativeState
              icon={post.status === "generating" ? Loader2 : Sparkles}
              spinning={post.status === "generating"}
              title={post.status === "generating" ? "Creative in progress" : "Creative queued"}
              description="The final render will appear here when this planned post completes."
            />
          ) : post.status === "failed" ? (
            <EmptyCreativeState
              icon={AlertCircle}
              title="Generation failed"
              description={latestAttempt?.lastError ?? "No error details are available yet."}
            />
          ) : (
            <EmptyCreativeState
              icon={ImageIcon}
              title="Creative not available yet"
              description="This planned post exists, but the final creative is not ready yet."
            />
          )}
          <CarouselSlideSummary
            carousel={latestCompleted?.carousel ?? null}
            selectedSlideId={carouselState.selectedSlideId}
            className="mt-4"
          />
        </div>

        <div className="grid gap-2 sm:grid-cols-3">
          <MetaPill
            icon={Target}
            label="Objective"
            title={`Objective: ${objectiveText}`}
            value={objectiveText}
          />
          <MetaPill
            icon={Users}
            label="Audience"
            title={`Audience: ${audienceText}`}
            value={audienceText}
          />
          <MetaPill
            icon={ImageIcon}
            label="Format"
            title={`Format: ${formatLabel}`}
            value={formatLabel}
          />
        </div>

        {showsPreviousSuccessWarning ? (
          <p className="text-sm leading-6 text-amber-900/95">
            The latest regeneration attempt failed. Showing the most recent successful render.
          </p>
        ) : null}

        <div
          className={cn(
            "grid gap-2",
            actionState.kind === "generate" ? "sm:grid-cols-2" : "sm:grid-cols-1",
          )}
        >
          <Button size="sm" className="justify-center rounded-xl" onClick={onOpenWorkspace}>
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
            Open Workspace
          </Button>
          {actionState.kind === "generate" ? (
            <Button
              variant="ghost"
              size="sm"
              className="justify-center rounded-xl bg-muted/55"
              onClick={onRegenerate}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {actionState.label}
            </Button>
          ) : null}
        </div>

        {actionState.kind === "status" ? (
          <p className="text-sm text-foreground/62">{actionState.label}</p>
        ) : null}

        <div className="border-t border-border/60 pt-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between px-0 text-foreground/62 hover:bg-transparent hover:text-foreground"
            onClick={onToggleDetails}
          >
            {expanded ? "Hide details" : "Show details"}
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", expanded ? "rotate-180" : "rotate-0")}
              aria-hidden="true"
            />
          </Button>

          {expanded ? (
            <div className="space-y-5 pt-4">
              <DetailSection label="Result Summary" value={summaryText} />
              <DetailSection label="Creative Objective" value={creativeObjectiveText} />
              <DetailSection label="Creative Rationale" value={rationaleText} />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyCreativeState({
  icon: Icon,
  title,
  description,
  spinning = false,
}: {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  description: string;
  spinning?: boolean;
}) {
  return (
    <div className="flex min-h-[18rem] flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <Icon
        className={cn(
          "h-8 w-8",
          spinning ? "animate-spin text-amber-600" : "text-muted-foreground",
        )}
        aria-hidden={true}
      />
      <div className="space-y-1">
        <div className="font-medium text-foreground">{title}</div>
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function MetaPill({
  icon: Icon,
  label,
  title,
  value,
}: {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  title: string;
  value: string;
}) {
  return (
    <div
      title={title}
      className="inline-flex min-w-0 items-start gap-2 rounded-xl bg-muted/55 px-3 py-2.5"
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground/50" aria-hidden={true} />
      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/48">
          {label}
        </span>
        <span className="block truncate text-[13px] leading-5 text-foreground/88">{value}</span>
      </span>
    </div>
  );
}

function DetailSection({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/48">
        {label}
      </div>
      <p className="text-[15px] leading-7 text-foreground/82">{value}</p>
    </div>
  );
}
